import { runInAction, makeAutoObservable } from "mobx"

import { BaseStore } from "stores/base_store";
import ProjectAPI from "api/project";
import { api_get } from "api/utils"
import { PagedResult } from "utils/types";

const Delay = (t: number) => new Promise(resolve => setTimeout(resolve, t));

export type CollabProps = {
    uuid: string;
    key: string;
    cls: string;
    variant: string;
    uniq: string;
    project: string;
    identity: string;
    title: string;
    description: string;
    cadence: string;
    completed: boolean;
    cached_collaborators?: string[];

    last_action_type: string;
    last_action_summary: string;
    last_action_date: string;
    last_action_identity: string;

    clock_total_seconds: number;

    date_created: string;
    date_updated: string;
    updateProps?: (attributes: CollabProps) => void;
};


// Domain object Collab
export class Collab implements CollabProps {
    key = "";
    cls = "";
    variant = "";
    uuid = "";
    uniq = "";
    project = "";
    identity = "";
    title = "";
    description = "";
    cadence = "";
    completed = false;
    cached_collaborators = [] as string[];
    last_action_type = "";
    last_action_summary = "";
    last_action_date = "";
    last_action_identity = "";
    date_created = "";
    date_updated = "";

    clock_total_seconds = 0;

    constructor(attributes: CollabProps) {
        makeAutoObservable(this, {
            updateProps: false
        })
        if (attributes) {
            this.updateProps(attributes);
        }
    }

    updateProps(attributes: CollabProps) {
        runInAction(() => {
            Object.assign(this, attributes);
        });
    }
}


export class CollabStore extends BaseStore<CollabProps, Collab> {
    FETCHED_COLLABS = [] as string[];
    _fetch_queue = [] as string[];
    _fetch_queue_timer = 0;

    async createCollab(project: string, identity: string, title: string, description: string, variant: string) {
        return ProjectAPI.createCollab(project, identity, title, description, variant).then((resp) => {
            this.receiveUpdate(resp);
            return resp;
        });
    }

    async createCollabByDedicated(workKey: string, project: string, identity: string, title: string, description: string) {
        return ProjectAPI.createCollabByDedicated(workKey, project, identity, title, description).then((resp) => {
            this.receiveUpdate(resp.collab);
            return resp;
        });
    }

    getCollab(key: string) {
        return this.items.find((c) => c.key === key || c.uuid === key)
    }

    getCollabsForProject(projectKey: string) {
        return this.items.filter((c) => c.project === projectKey)
    }

    getCollabsByVariant(identity: string, variant: string) {
        return this.items
            .filter((c) => c.variant === variant && c.identity === identity)
            .sort((a, b) => {
                return a.date_created > b.date_created ? -1 : 1;
            });
    }

    async supportFetchCollabs(workKey: string, lookup_identity: string, cursor = "") {
        const params = { identity: workKey, lookup_identity, cursor };
        return api_get("/collab/support/list/by-identity", params)
        .then(async (res) => {
            if (res.ok) {
                const data = await res.json();
                this.receiveUpdate(data.items);
                return data as PagedResult<CollabProps>;
            }
        });
    }

    async fetchCollabs(identityKey: string, projectKey: string, cursor = "") {
        ProjectAPI.getCollabs(identityKey, projectKey, cursor).then((resp) => {
            this.receiveUpdate(resp.items);
            if (resp.cursor) {
                this.fetchCollabs(identityKey, projectKey, resp.cursor);
            }
            return resp.collabs;
        })
    }

    async fetchCollabsByVariant(identity: string, variant: string, cursor = "") {
        const data = { identity, variant, cursor };
        const response = await api_get("/collab/list/by-variant", data);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            return data;
        }
        return null;
    }

    async fetchCollab(identityKey: string, key: string) {
        if (!key) {
            console.error("missing key");
            return null;
        }

        if (!identityKey) {
            console.error("missing identityKey");
            return null;
        }

        if (this.FETCHED_COLLABS.includes(key))
            return null;
        this.FETCHED_COLLABS.push(key);
        this._add_to_fetch_queue(key);

        if (!this._fetch_queue_timer) {
            this._fetch_queue_timer = 1;
            return Delay(100).then(() => {
                this.doFetchQueue(identityKey);
                this._fetch_queue_timer = 0;
                return true;
            });
        }
        return Promise.resolve(true);
    }


    async doFetchQueue(identityKey: string) {
        const items = this._fetch_queue;
        this._fetch_queue = [];

        if(items.length === 0) {
            console.log("empty fetch queue");
            return;
        }

        ProjectAPI.getCollabsByKeyList(identityKey, items)
            .then((data: { items: CollabProps[] }) => {
                this.receiveUpdate(data.items);
                if (this._fetch_queue.length > 0) {
                    this._fetch_queue_timer = window.setTimeout(() => {
                        this.doFetchQueue(identityKey);
                    }, 500);
                }
                else {
                    this._fetch_queue_timer = 0;
                }
            })
            .catch((err) => {
                console.error("error fetching list: %o", err);
                console.error("identity: %o", identityKey);
                console.error("items: %o", items);
            });

    }


    _add_to_fetch_queue(key: string) {
        if(this._fetch_queue.includes(key))
            return;
        this._fetch_queue.push(key);
    }

    async fetchCollabsWith(identityKey: string,
        mututalIdentityKey: string,
        cursor = "") {
        return ProjectAPI.fetchCollabsWith(identityKey, mututalIdentityKey, cursor).then((resp) => {
            this.receiveUpdate(resp.items);
            if(resp.cursor) {
                this.fetchCollabsWith(identityKey, mututalIdentityKey, resp.cursor);
            }
        })
    }


    modelConstructor() {
        return Collab;
    }

}
